(ns common.schema.entities.kyc.kyc-profile.materialization
  (:require [common.time :as c-time]
            [common.data-types :as data-types]
            [common.dates :as dates]
            [common.log :as log]))

(defn- tenant-id
  [kyc-profile]
  (let [tenant (:common/tenant kyc-profile)]
    (or (:db/id tenant) tenant)))

(defn- log-context
  "Identifies the records a warning is about, so the offending value can be
   found and corrected rather than only noted."
  [kyc-profile risk-category]
  (str "kyc-profile=" (:db/id kyc-profile)
       " risk-category=" (:db/id risk-category)
       " tenant=" (tenant-id kyc-profile)))

(defn- stored-number
  "Reads a field that may have been stored as a number or as an edn string.

   Returns nil for an absent field - ordinary data with nothing to compute, and
   the case a profile with no risk category hits on every materialization - and
   nil plus a WARN for a present value that does not resolve to a number, which
   is a record someone has to correct.

   Returning nil for that second case is also what keeps it away from
   `dates/date-time-from-epoch-millis`, which throws IllegalArgumentException
   out of java.util.Date when handed one."
  [{:keys [value attribute kyc-profile risk-category]}]
  (let [resolved (cond (number? value)
                       value
                       (string? value)
                       (data-types/parse-edn-string value))]
    (cond (number? resolved)
          resolved
          (some? value)
          (do (log/warn "KYC profile expiry: unusable"
                        attribute
                        (pr-str value)
                        (type value)
                        (log-context kyc-profile risk-category))
              nil))))

(defn kyc-profile-expires-epoch
  [kyc-profile risk-category]
  (let [approval-millis (stored-number
                          {:value         (:kyc-profile/approval-date kyc-profile)
                           :attribute     :kyc-profile/approval-date
                           :kyc-profile   kyc-profile
                           :risk-category risk-category})
        approval-date (when approval-millis
                        (dates/date-time-from-epoch-millis approval-millis))
        renewal-interval (stored-number
                           {:value         (:kyc-risk-category/renewal-interval
                                             risk-category)
                            :attribute     :kyc-risk-category/renewal-interval
                            :kyc-profile   kyc-profile
                            :risk-category risk-category})
        res-date (when (and approval-date renewal-interval)
                   (dates/plus
                     approval-date
                     (dates/years renewal-interval)))
        res-date-epoch (when res-date
                         (dates/epoch-millis res-date))
        risk-category-changed-on (:kyc-profile/risk-category-changed-on kyc-profile)]
    (cond (and (number? risk-category-changed-on)
               (number? res-date-epoch))
          (max risk-category-changed-on res-date-epoch)
          (number? res-date-epoch)
          res-date-epoch
          :else
          nil)))

(defn kyc-profile-expired-over-three-months-ago?
  [{:keys [kyc-profile entity-lookup]}]
  (let [risk-category-id (:kyc-profile/risk-category kyc-profile)
        risk-category (entity-lookup risk-category-id)
        expiry-epoch (kyc-profile-expires-epoch kyc-profile risk-category)]
    (when (number? expiry-epoch)
      (let [now (c-time/now-millis)
            approx-three-months-ago (- now (* 1000 3600 24 30 3))
            expired-over-three-months-ago? (< expiry-epoch approx-three-months-ago)]
        expired-over-three-months-ago?))))

(defn kyc-profile-has-sanctioned-entities?
  [{:keys [kyc-profile reverse-lookup]}]
  (let [screening-profiles (->> (reverse-lookup
                                  {:entity                 kyc-profile
                                   :attribute-entity-types [:entity.type/screening-profile]
                                   :attribute              :screening-profile/kyc-profiles})
                                (remove :common/disabled?))
        sanction-hits? (->> screening-profiles
                            (some (fn [screening-profile]
                                    (-> screening-profile
                                        :screening-profile/sanction-status
                                        (= :sanction-status/red)))))]
    sanction-hits?))


(defn calc-blocking-status-fn
  [{:keys [entity-data entity-lookup reverse-lookup]}]
  (let [blocking-status (:kyc-profile/blocking-status entity-data)]
    (if (contains? #{:blocking-status/blocked :blocking-status/not-blocked} blocking-status)
      blocking-status
      (let [expired-over-three-months-ago? (kyc-profile-expired-over-three-months-ago?
                                             {:kyc-profile   entity-data
                                              :entity-lookup entity-lookup})
            sanctions? (kyc-profile-has-sanctioned-entities?
                         {:kyc-profile    entity-data
                          :reverse-lookup reverse-lookup})
            blocked? (or expired-over-three-months-ago?
                         sanctions?)]
        (if blocked?
          :blocking-status/blocked
          :blocking-status/not-blocked)))))
